import { __awaiter, __extends, __generator } from "tslib";
import * as AugLoopUtils from './utils/AugLoopUtils';
import { BatchTranscriptionResult, BatchTranscriptionErrorCode } from '@augloop-types/voice';
import { v4 as uuidv4 } from 'uuid';
export var BatchTranscriptionClientErrorCode;
(function (BatchTranscriptionClientErrorCode) {
    BatchTranscriptionClientErrorCode["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
    BatchTranscriptionClientErrorCode["UNSUPPORTED_FILE_FORMAT"] = "UNSUPPORTED_FILE_FORMAT";
    BatchTranscriptionClientErrorCode["INVALID_REQUEST"] = "INVALID_REQUEST";
    BatchTranscriptionClientErrorCode["REQUEST_DISPOSED"] = "REQUEST_DISPOSED";
    BatchTranscriptionClientErrorCode["POLLER_DISPOSED"] = "POLLER_DISPOSED";
    BatchTranscriptionClientErrorCode["PROCESSING"] = "PROCESSING";
    BatchTranscriptionClientErrorCode["DELETED"] = "DELETED";
})(BatchTranscriptionClientErrorCode || (BatchTranscriptionClientErrorCode = {}));
var BatchTranscriptionClientError = /** @class */ (function (_super) {
    __extends(BatchTranscriptionClientError, _super);
    function BatchTranscriptionClientError(code, message) {
        var _this = _super.call(this, message) || this;
        _this.code = code;
        return _this;
    }
    return BatchTranscriptionClientError;
}(Error));
export { BatchTranscriptionClientError };
var alActivationTokens = [];
export function initializeBatchTranscriptionClient(alRuntimeManager) {
    return __awaiter(this, void 0, void 0, function () {
        var _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _c = (_b = alActivationTokens).push;
                    return [4 /*yield*/, AugLoopUtils.activateAnnotationAsync(alRuntimeManager, BatchTranscriptionResult.getTypeName())];
                case 1:
                    _c.apply(_b, [_d.sent()]);
                    // Send empty tile to warm up workflow
                    AugLoopUtils.submitBatchTranscriptionConversation(alRuntimeManager, '', uuidv4());
                    return [2 /*return*/];
            }
        });
    });
}
export function uninitializeBatchTranscriptionClient(alRuntimeManager) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_b) {
            AugLoopUtils.releaseAnnotations(alRuntimeManager, alActivationTokens);
            return [2 /*return*/];
        });
    });
}
var BatchTranscriptionClientRequest = /** @class */ (function () {
    // Constructs BatchTranscriptionClientRequest for the given conversation ID.
    function BatchTranscriptionClientRequest(alRuntimeManager, conversationId, speechFdUrl, logMessage) {
        this.getResultCallbacks = null;
        this.conversationId = conversationId;
        this.alRuntimeManager = alRuntimeManager;
        this.speechFdUrl = speechFdUrl;
        this.logMessage = logMessage;
    }
    // Make this request and return a promise that will be resolved to the result
    BatchTranscriptionClientRequest.prototype.makeRequest = function () {
        var _this = this;
        if (this.getResultCallbacks !== null) {
            throw new BatchTranscriptionClientError(BatchTranscriptionClientErrorCode.INVALID_REQUEST, 'getResult can only be called once per instance. If you want to make another request, create another instance.');
        }
        return new Promise(function (resolve, reject) {
            // Create request ID and submit the request tile.
            var requestId = uuidv4();
            AugLoopUtils.submitBatchTranscriptionConversation(_this.alRuntimeManager, _this.conversationId, requestId, _this.speechFdUrl);
            _this.getResultCallbacks = { resolve: resolve, reject: reject, requestId: requestId };
        });
    };
    BatchTranscriptionClientRequest.prototype.dispose = function () {
        if (this.getResultCallbacks != null) {
            this.getResultCallbacks.reject(new BatchTranscriptionClientError(BatchTranscriptionClientErrorCode.REQUEST_DISPOSED, 'Caller disposed the BatchTranscriptionClientRequest instance'));
        }
        this.getResultCallbacks = null;
    };
    // Caller must forward augloop annotation results using this function.
    BatchTranscriptionClientRequest.prototype.onAnnotationResult = function (operation) {
        var _this = this;
        var batchTranscriptionItems = AugLoopUtils.filterOperationByAnnotationTypeName(operation, BatchTranscriptionResult.getTypeName());
        // BatchTranscriptionConversation tiles have the request ID as tile ID.
        var requestId = operation.parentPath[operation.parentPath.length - 1];
        batchTranscriptionItems.forEach(function (item) {
            var result = item.body;
            // Nothing to do if there are no callbacks.
            // If this annotation is not for the request this object tracks, ignore it.
            if (_this.getResultCallbacks == null || _this.getResultCallbacks.requestId !== requestId) {
                if (result.conversationId === _this.conversationId && _this.logMessage) {
                    _this.logMessage("Dropped response for conversation ID that didn't belong to this request. Should we reuse results for the same conversation ID across requests?");
                }
                return;
            }
            try {
                handleBatchTranscriptionResultError(result);
            }
            catch (e) {
                _this.getResultCallbacks.reject(e);
            }
            try {
                var transcriptSegments = parseBatchTranscriptionResultSegments(result);
                _this.getResultCallbacks.resolve({
                    transcriptSegments: transcriptSegments,
                });
            }
            catch (e) {
                _this.getResultCallbacks.reject(new BatchTranscriptionClientError(BatchTranscriptionClientErrorCode.UNKNOWN_ERROR, 'Error parsing workflow response ' + e.message));
            }
        });
    };
    return BatchTranscriptionClientRequest;
}());
export { BatchTranscriptionClientRequest };
function clientErrorCodeFromServerErrorCode(code) {
    switch (code) {
        case BatchTranscriptionErrorCode.UNSUPPORTED_FILE_FORMAT:
            return BatchTranscriptionClientErrorCode.UNSUPPORTED_FILE_FORMAT;
        case BatchTranscriptionErrorCode.PROCESSING:
            return BatchTranscriptionClientErrorCode.PROCESSING;
        default:
            return BatchTranscriptionClientErrorCode.UNKNOWN_ERROR;
    }
}
function parseBatchTranscriptionResultSegments(result) {
    if (!result.transcriptionsJson) {
        throw new BatchTranscriptionClientError(BatchTranscriptionClientErrorCode.UNKNOWN_ERROR, 'Received invalid transcriptionsJson');
    }
    var parsedTranscripts = JSON.parse(result.transcriptionsJson);
    return parsedTranscripts.map(function (segment) {
        return {
            offsetInTicks: segment.offsetInTicks,
            durationInTicks: segment.durationInTicks,
            userId: segment.userId,
            text: segment.text,
        };
    });
}
function handleBatchTranscriptionResultError(result) {
    if (result.errorCode) {
        throw new BatchTranscriptionClientError(clientErrorCodeFromServerErrorCode(result.errorCode), result.errorMessage);
    }
}
var BatchTranscriptionClientRequestPoller = /** @class */ (function () {
    function BatchTranscriptionClientRequestPoller(pollingDelayMilliseconds, alRuntimeManager, conversationId, speechFdUrl, logMessage) {
        this.isDisposed = false;
        this.lastRequest = null;
        this.pollingDelayMilliseconds = pollingDelayMilliseconds;
        this.conversationId = conversationId;
        this.alRuntimeManager = alRuntimeManager;
        this.speechFdUrl = speechFdUrl;
        this.logMessage = logMessage;
    }
    BatchTranscriptionClientRequestPoller.prototype.makeRequest = function () {
        return __awaiter(this, void 0, void 0, function () {
            var lastError, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        lastError = null;
                        _b.label = 1;
                    case 1:
                        if (!(lastError != null)) return [3 /*break*/, 3];
                        return [4 /*yield*/, wait(this.pollingDelayMilliseconds)];
                    case 2:
                        _b.sent();
                        _b.label = 3;
                    case 3:
                        this.throwIfDisposed();
                        this.lastRequest = new BatchTranscriptionClientRequest(this.alRuntimeManager, this.conversationId, this.speechFdUrl, this.logMessage);
                        _b.label = 4;
                    case 4:
                        _b.trys.push([4, 6, , 7]);
                        return [4 /*yield*/, this.lastRequest.makeRequest()];
                    case 5: return [2 /*return*/, _b.sent()];
                    case 6:
                        error_1 = _b.sent();
                        if (error_1.code === BatchTranscriptionClientErrorCode.REQUEST_DISPOSED)
                            this.throwIfDisposed();
                        lastError = error_1;
                        return [3 /*break*/, 7];
                    case 7:
                        if (lastError.code === BatchTranscriptionClientErrorCode.PROCESSING) return [3 /*break*/, 1];
                        _b.label = 8;
                    case 8: throw lastError;
                }
            });
        });
    };
    // Caller must forward augloop annotation results using this function.
    BatchTranscriptionClientRequestPoller.prototype.onAnnotationResult = function (operation) {
        if (this.lastRequest != null)
            this.lastRequest.onAnnotationResult(operation);
    };
    BatchTranscriptionClientRequestPoller.prototype.dispose = function () {
        this.isDisposed = true;
        if (this.lastRequest != null)
            this.lastRequest.dispose();
    };
    BatchTranscriptionClientRequestPoller.prototype.throwIfDisposed = function () {
        if (!this.isDisposed)
            return;
        throw new BatchTranscriptionClientError(BatchTranscriptionClientErrorCode.POLLER_DISPOSED, 'Caller disposed the BatchTranscriptionClientPoller instance');
    };
    return BatchTranscriptionClientRequestPoller;
}());
export { BatchTranscriptionClientRequestPoller };
function wait(waitTimeMs) {
    return new Promise(function (resolve) {
        setTimeout(resolve, waitTimeMs);
    });
}
//# sourceMappingURL=BatchTranscriptionClient.js.map